import classNames from 'classnames';
import * as _ from 'lodash-es';
import React from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '../../graphql/generated/gql';
import PostPreviewQuillComponent from '../../post/PostPreviewQuillComponent';
import QuillContext from '../QuillContext';
import EDITOR_INTEGRATION_MENTIONS from '../mentions/EditorIntegrationMentions';
import * as styles from './EmbedComponent.module.scss';
import EmbedFallback from './EmbedFallback';
import IframeWithFallback from './IframeWithFallback';
import type { EmbedValue, PreviewMode } from './matchEmbed';
import { EmbedMode, embedUrl } from './matchEmbed';

type RenderMode = 'image' | 'content' | 'embed';

export const getEmbedPresentationOptions = (type: string) => {
  const renderModes: {
    [key: string]: RenderMode;
  } = {
    gliffy: 'image',
    'quip-thread': 'content',
    'zendesk-article': 'content',
  };

  return {
    allowXSmallHeight: ['google-drive-folder', 'twitter'].includes(type),
    renderMode: renderModes[type] || 'embed',
  };
};

const GET_EMBED_QUERY = gql(/** @complexity 8 */ `
  query getEmbedContent($type: MentionType!, $id: ID!) {
    mention(type: $type, id: $id) {
      id
      preview {
        content
      }
    }
  }
`);

interface EmbedComponentScaleContainerProps {
  children: React.ReactNode;
}

export const EmbedComponentScaleContainer = ({
  children,
}: EmbedComponentScaleContainerProps) => {
  return (
    <div className={styles.scaleContainer}>
      <div className={styles.scaledContent}>{children}</div>
    </div>
  );
};

interface EmbedComponentProps extends EmbedValue {
  readOnly: boolean;
  className?: string;
  previewMode?: PreviewMode;
  selected?: boolean;
}

const EmbedComponent = ({
  id,
  type,
  params,
  readOnly,
  className,
  previewMode,
  selected,
}: EmbedComponentProps) => {
  const { renderMode } = getEmbedPresentationOptions(type);

  const { apiEnum } = EDITOR_INTEGRATION_MENTIONS[type] || {};
  const { data, loading, error, refetch } = useQuery(GET_EMBED_QUERY, {
    // @ts-expect-error
    variables: { id, type: apiEnum },
    skip: !apiEnum || renderMode !== 'content',
  });

  const src = embedUrl(
    { id, type, params },
    readOnly ? EmbedMode.READONLY : EmbedMode.EDIT,
    previewMode,
  );
  if (!src) return null;

  const title = _.startCase(type);
  if (renderMode === 'image') {
    return <img src={src} alt={title} className={className} />;
  }

  if (renderMode === 'embed') {
    return (
      <IframeWithFallback
        allowFullScreen
        frameBorder="0"
        src={src}
        title={title}
        className={className}
        type={type}
        selected={selected}
        fallback={
          <div className={className}>
            <EmbedFallback
              id={id}
              type={type}
              error={error}
              refetch={refetch}
            />
          </div>
        }
      />
    );
  }

  const content = data?.mention?.preview?.content;

  return (
    <div className={classNames(styles.container, className)}>
      {!loading && (
        <>
          {content ? (
            <EmbedComponentScaleContainer>
              <QuillContext>
                <PostPreviewQuillComponent content={content} />
              </QuillContext>
            </EmbedComponentScaleContainer>
          ) : (
            <EmbedFallback
              id={id}
              type={type}
              refetch={refetch}
              error={error}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EmbedComponent;
